import { useCallback, useEffect, useRef, useState } from 'react'

import { type Dictation, type DictationState, dictationSupported, startDictation } from '@/lib/dictation'

export enum DictationStatus {
  IDLE = 'idle',
  STARTING = 'starting',
  LISTENING = 'listening',
  FINISHING = 'finishing',
}

/** Dictation for a message box: what was said is handed to `onText` when the person is done. */
export function useDictation(onText: (text: string) => void) {
  const [supported] = useState(dictationSupported)
  const [status, setStatus] = useState(DictationStatus.IDLE)
  const [state, setState] = useState<DictationState>({ text: '', level: 0, loading: null })
  const [error, setError] = useState<string | null>(null)
  const dictation = useRef<Dictation | null>(null)
  // Counts starts and cancels, so a dictation canceled while the microphone or the model is still on its way
  // neither listens nor writes anything once it gets there.
  const session = useRef(0)

  const cancel = useCallback(() => {
    session.current += 1
    dictation.current?.cancel()
    dictation.current = null
    setStatus(DictationStatus.IDLE)
  }, [])

  // Leaving the page stops the microphone.
  useEffect(
    () => () => {
      session.current += 1
      dictation.current?.cancel()
    },
    []
  )

  const finish = async () => {
    const current = dictation.current
    if (!current) return
    const finishing = session.current
    setStatus(DictationStatus.FINISHING)
    try {
      const text = await current.finish()
      if (text && finishing === session.current) onText(text)
    } catch {
      if (finishing === session.current)
        setError('Dictation could not be loaded. Try again, or type instead.')
    } finally {
      if (finishing === session.current) {
        dictation.current = null
        setStatus(DictationStatus.IDLE)
      }
    }
  }

  const start = async () => {
    if (status !== DictationStatus.IDLE) return
    const starting = (session.current += 1)
    setError(null)
    setState({ text: '', level: 0, loading: null })
    setStatus(DictationStatus.STARTING)
    try {
      const started = await startDictation({ onChange: setState, onStop: () => void finish() })
      if (starting !== session.current) return started.cancel()
      dictation.current = started
      setStatus(DictationStatus.LISTENING)
    } catch {
      if (starting !== session.current) return
      setStatus(DictationStatus.IDLE)
      setError('Allow the microphone to dictate.')
    }
  }

  return { supported, status, ...state, error, start, finish, cancel }
}

export type DictationControls = ReturnType<typeof useDictation>
